import { useEffect } from 'react';
import { defaultParseSearch, useNavigate, useRouterState } from '@tanstack/react-router';
import { CAMPAIGN_TABS, legacySettingsSub } from './search';
import { CRM_SEARCH_KEYS } from './useCrmViewParam';
import { OUTREACH_SUB_VIEWS } from './useOutreachSubParam';

/** Params that only mean something inside the tab they were written for. */
const VIEW_LOCAL_KEYS: readonly string[] = ['sub', 'entry', 'template', 'pill', 'page', ...CRM_SEARCH_KEYS];
const OBJECT_ID = /^[0-9a-f]{24}$/i;

const isOneOf = (value: unknown, allowed: readonly string[]) => typeof value === 'string' && allowed.includes(value);

/** Spec §7a hygiene: the raw (pre-validation) search minus every duplicate or
 *  unknown tab/sub/entry, or null when nothing needs dropping. */
export function cleanCampaignSearch(raw: Record<string, unknown>): Record<string, unknown> | null {
  // Redirected by CampaignDetailPage's own legacy effect — never cleaned here.
  if (legacySettingsSub(raw) !== null) return null;
  const badTab = raw.tab !== undefined && !isOneOf(raw.tab, CAMPAIGN_TABS);
  const onOutreach = !badTab && raw.tab === 'outreach';
  const badSub =
    raw.sub !== undefined && (typeof raw.sub !== 'string' || (onOutreach && !isOneOf(raw.sub, OUTREACH_SUB_VIEWS)));
  const badEntry = raw.entry !== undefined && (typeof raw.entry !== 'string' || !OBJECT_ID.test(raw.entry));
  const drop = new Set<string>([
    ...(badTab ? ['tab', ...VIEW_LOCAL_KEYS] : []),
    ...(badSub ? ['sub'] : []),
    ...(badEntry ? ['entry'] : []),
  ]);
  if (drop.size === 0) return null;
  return Object.fromEntries(Object.entries(raw).filter(([key]) => !drop.has(key)));
}

/** Parses the location's own query string rather than trusting the validated
 *  `location.search` object — `campaignSearchSchema`'s per-field `.catch()`
 *  already substitutes a safe default for anything it rejects (e.g. a bad
 *  `tab` silently becomes `'creators'`), so reading the validated object
 *  would make the original bad value invisible to clean. Delegates to the
 *  router's own `defaultParseSearch` (same as `parseSearchFirstValue`)
 *  rather than a hand-rolled `URLSearchParams` split: TanStack's writer
 *  JSON-quotes a numeric-looking string value (e.g. an all-digit ObjectId)
 *  to round-trip it as a string, and only `defaultParseSearch` undoes that
 *  quoting — a plain `URLSearchParams` read would keep the literal quote
 *  characters and fail `OBJECT_ID.test`, dropping a perfectly valid `entry`.
 *  A repeated key still becomes an array, the same shape a duplicate `tab`
 *  produces here. */
function rawSearchFromQueryString(searchStr: string): Record<string, unknown> {
  return defaultParseSearch(searchStr) as Record<string, unknown>;
}

export function useCampaignSearchHygiene(): void {
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  const navigate = useNavigate();
  useEffect(() => {
    const cleaned = cleanCampaignSearch(rawSearchFromQueryString(searchStr));
    if (!cleaned) return;
    // `as never`: this router-agnostic package cannot reference the app's route tree (same as useCampaignDeepLinkTab).
    void navigate({ to: '.', search: cleaned, replace: true } as never);
  }, [searchStr, navigate]);
}
